import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, TextField, InputAdornment } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSite } from '../context/SiteContext';
import useAuthGuard from '../hooks/useAuthGuard';
import usePageReady from '../hooks/usePageReady';
import api from '../services/api';
import Footer from '../components/Footer';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('govirtual_user') || sessionStorage.getItem('govirtual_user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const timeToMinutes = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return isNaN(h) || isNaN(m) ? null : h * 60 + m;
};

/**
 * Find the closest session to display:
 * 1. Currently live (now between startTime and endTime)
 * 2. Most recently ended (closest in the past)
 * 3. Next upcoming (closest in the future)
 * 4. null if no sessions at all
 */
const getClosestSession = (schedule = [], plenaryPage) => {
  const now     = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();

  // Filter relevant sessions for this plenary
  const relevant = schedule.filter(item => {
    if (plenaryPage?.sessionIds?.length > 0) return plenaryPage.sessionIds.includes(item.id);
    // Default: match by location name or show all if no location set
    if (!item.location) return true;
    return item.location?.toLowerCase() === plenaryPage?.name?.toLowerCase();
  });

  if (!relevant.length) return { session: null, status: 'offline' };

  // 1. Currently live
  const live = relevant.find(item => {
    const start = timeToMinutes(item.startTime);
    const end   = timeToMinutes(item.endTime);
    if (start === null) return false;
    if (end === null)   return nowMins >= start;
    return nowMins >= start && nowMins < end;
  });
  if (live) return { session: live, status: 'live' };

  // 2. Most recently ended (closest past)
  const past = relevant
    .filter(item => timeToMinutes(item.endTime) !== null && timeToMinutes(item.endTime) <= nowMins)
    .sort((a, b) => timeToMinutes(b.endTime) - timeToMinutes(a.endTime));
  
  // 3. Next upcoming (closest future)
  const upcoming = relevant
    .filter(item => timeToMinutes(item.startTime) > nowMins)
    .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  // Show upcoming if within 2 hours, else show most recently ended
  if (upcoming.length && (!past.length || timeToMinutes(upcoming[0].startTime) - nowMins <= 120)) {
    return { session: upcoming[0], status: 'upcoming' };
  }
  if (past.length) return { session: past[0], status: 'ended' };
  if (upcoming.length) return { session: upcoming[0], status: 'upcoming' };

  return { session: null, status: 'offline' };
};

// ─── Video Player (Mux / iframe / HLS) ───────────────────────────────────────
const VideoPlayer = ({ streamUrl, streamType, viewerCount, primary }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted,   setMuted]   = useState(false);
  const [progress,setProgress]= useState(0);
  const [duration,setDuration]= useState(0);

  useEffect(() => {
    if (!streamUrl || !videoRef.current || streamType !== 'hls') return;
    const video = videoRef.current;
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = streamUrl;
    } else {
      import('https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.min.js')
        .then(({ default: Hls }) => {
          if (Hls.isSupported()) { const h = new Hls(); h.loadSource(streamUrl); h.attachMedia(video); }
        })
        .catch(() => { video.src = streamUrl; });
    }
  }, [streamUrl, streamType]);

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  // ── Mux / any iframe embed ────────────────────────────────────────────────
  if (streamType === 'iframe' || streamType === 'youtube' || streamType === 'vimeo') {
    return (
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', bgcolor: '#000', borderRadius: 2, overflow: 'hidden' }}>
        {streamUrl ? (
          <>
            <Box
              component="iframe"
              src={streamUrl}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              sx={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
            {/* LIVE badge + viewer count over iframe */}
            <Box sx={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 1, alignItems: 'center', pointerEvents: 'none' }}>
              <Box sx={{ bgcolor: '#EF4444', color: '#fff', fontSize: 11, fontWeight: 800, px: 1.25, py: 0.35, borderRadius: 1, letterSpacing: 0.5 }}>
                ● LIVE
              </Box>
              {viewerCount > 0 && (
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 11, px: 1.25, py: 0.35, borderRadius: 1 }}>
                  {viewerCount >= 1000 ? `${(viewerCount / 1000).toFixed(1)}k` : viewerCount} Watching
                </Box>
              )}
            </Box>
          </>
        ) : (
          <OfflinePlayer />
        )}
      </Box>
    );
  }

  // ── HLS / native video ────────────────────────────────────────────────────
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', bgcolor: '#000', borderRadius: 2, overflow: 'hidden' }}>
      {streamUrl ? (
        <>
          <Box component="video" ref={videoRef} muted={muted}
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
            onTimeUpdate={e => setProgress(e.target.currentTime)}
            onDurationChange={e => setDuration(e.target.duration)}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'pointer' }}
            onClick={() => videoRef.current?.[playing ? 'pause' : 'play']()}
          />
          {!playing && (
            <Box onClick={() => videoRef.current?.play()}
              sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 0, height: 0, borderTop: '14px solid transparent', borderBottom: '14px solid transparent', borderLeft: '22px solid rgba(255,255,255,0.9)', ml: 0.5 }} />
              </Box>
            </Box>
          )}
          <Box sx={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ bgcolor: '#EF4444', color: '#fff', fontSize: 11, fontWeight: 800, px: 1.25, py: 0.35, borderRadius: 1, letterSpacing: 0.5 }}>● LIVE</Box>
            {viewerCount > 0 && (
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, px: 1.25, py: 0.35, borderRadius: 1 }}>
                {viewerCount >= 1000 ? `${(viewerCount / 1000).toFixed(1)}k` : viewerCount} Watching
              </Box>
            )}
          </Box>
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)', px: 2, pb: 1.5, pt: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton size="small" onClick={() => setMuted(m => !m)} sx={{ color: '#fff', p: 0.5 }}>{muted ? '🔇' : '🔊'}</IconButton>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, minWidth: 80 }}>{fmt(progress)} / {fmt(duration)}</Typography>
            <Box sx={{ flex: 1, height: 4, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 2, cursor: 'pointer' }}
              onClick={e => { const r = e.currentTarget.getBoundingClientRect(); if (videoRef.current) videoRef.current.currentTime = ((e.clientX - r.left) / r.width) * duration; }}>
              <Box sx={{ width: `${(progress / (duration || 1)) * 100}%`, height: '100%', bgcolor: primary, borderRadius: 2 }} />
            </Box>
            <IconButton size="small" onClick={() => videoRef.current?.requestFullscreen()} sx={{ color: '#fff', p: 0.5, fontSize: 16 }}>⛶</IconButton>
          </Box>
        </>
      ) : <OfflinePlayer />}
    </Box>
  );
};

const OfflinePlayer = () => (
  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0D1B2A', flexDirection: 'column', gap: 1.5 }}>
    <Typography sx={{ fontSize: 40 }}>📺</Typography>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', letterSpacing: 1, textTransform: 'uppercase', fontSize: 11 }}>Stream not available</Typography>
  </Box>
);

// ─── Chat Panel ───────────────────────────────────────────────────────────────
const ChatPanel = ({ plenaryPage, primary, chatEmbedUrl, chatTitle }) => {
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [sending,  setSending]  = useState(false);
  const bottomRef = useRef(null);
  const user = getStoredUser();

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/plenary/${plenaryPage._id}/chat?limit=50`);
      setMessages(res.data.data || []);
    } catch {}
  }, [plenaryPage._id]);

  useEffect(() => {
    loadMessages();
    const id = setInterval(loadMessages, 4000);
    return () => clearInterval(id);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const name = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email : 'Guest';
    setSending(true);
    try {
      await api.post(`/plenary/${plenaryPage._id}/chat`, { message: input.trim(), authorName: name });
      setInput('');
      await loadMessages();
    } catch {} finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }); }
    catch { return ''; }
  };

  if (chatEmbedUrl) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>{chatTitle || 'Live Discussion'}</Typography>
          <Box sx={{ ml: 'auto', bgcolor: '#22C55E', color: '#fff', fontSize: 10, fontWeight: 700, px: 1, py: 0.25, borderRadius: 100 }}>ACTIVE</Box>
        </Box>
        <Box component="iframe" src={chatEmbedUrl} sx={{ flex: 1, border: 'none', display: 'block' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Box component="span" sx={{ fontSize: 16 }}>💬</Box>
        <Typography variant="subtitle2" fontWeight={700}>{chatTitle || 'Live Discussion'}</Typography>
        <Box sx={{ ml: 'auto', bgcolor: '#22C55E', color: '#fff', fontSize: 10, fontWeight: 700, px: 1, py: 0.25, borderRadius: 100, letterSpacing: 0.5 }}>ACTIVE</Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {messages.map((msg, i) => (
          <Box key={msg._id || i}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: msg.isStaff ? '#B45309' : primary, fontSize: 12 }}>
                {msg.authorName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>{fmtTime(msg.createdAt)}</Typography>
            </Box>
            <Box sx={{ bgcolor: msg.isStaff ? '#FFFBEB' : '#F8FAFC', border: '1px solid', borderColor: msg.isStaff ? '#FDE68A' : '#EEF2F7', borderRadius: '0 8px 8px 8px', px: 1.5, py: 1 }}>
              <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.5 }}>{msg.message}</Typography>
            </Box>
          </Box>
        ))}
        <div ref={bottomRef} />
      </Box>
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <TextField fullWidth size="small" placeholder="Join the conversation..."
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={sendMessage} disabled={!input.trim() || sending} sx={{ color: primary }}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: { borderRadius: 100, bgcolor: '#F8FAFC' },
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Session Info ─────────────────────────────────────────────────────────────
const SessionInfo = ({ session, status, plenaryPage, primary, eventSpeakers = [] }) => {
  if (status === 'offline' || !session) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', letterSpacing: 1.5, textTransform: 'uppercase', fontSize: 10, display: 'block', mb: 0.75 }}>
          No Active Session
        </Typography>
        <Typography variant="h5" sx={{ color: '#fff', mb: 1 }}>
          {plenaryPage.offlineTitle || 'Session Starting Soon'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
          {plenaryPage.offlineMessage || 'Please check the schedule for upcoming sessions.'}
        </Typography>
      </Box>
    );
  }

  // Resolve speaker objects from event.speakers by name
  const speakers = (session.speakers || [])
    .map(name => eventSpeakers.find(s => s.name === name))
    .filter(Boolean);

  return (
    <Box sx={{ mt: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {/* Poster image */}
      {session.posterUrl && (
        <Box sx={{ flexShrink: 0, width: { xs: 80, md: 100 }, height: { xs: 80, md: 100 }, borderRadius: 2, overflow: 'hidden' }}>
          <Box component="img" src={session.posterUrl} alt={session.title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </Box>
      )}

      {/* Text + speakers */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Status badge + context */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          {status === 'live' && (
            <Box sx={{ bgcolor: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800, px: 1.25, py: 0.3, borderRadius: 1, letterSpacing: 0.5 }}>
              ● LIVE
            </Box>
          )}
          {status === 'upcoming' && (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 700, px: 1.25, py: 0.3, borderRadius: 1, letterSpacing: 0.5 }}>
              UP NEXT
            </Box>
          )}
          {status === 'ended' && (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 700, px: 1.25, py: 0.3, borderRadius: 1, letterSpacing: 0.5 }}>
              ENDED
            </Box>
          )}
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase', fontSize: 10 }}>
            {status === 'upcoming'
              ? `Starts at ${session.startTime}`
              : session.location || session.type || 'Keynote Presentation'}
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ color: '#fff', mb: 1, lineHeight: 1.2, fontSize: { xs: '1.3rem', md: '1.6rem' } }}>
          {session.title}
        </Typography>

        {session.description && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, mb: 1.5 }}>
            {session.description}
          </Typography>
        )}

        {/* Speaker avatars + names */}
        {speakers.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex' }}>
              {speakers.slice(0, 5).map((s, i) => (
                <Box key={i} title={s.name}
                  sx={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(255,255,255,0.2)', ml: i > 0 ? -1 : 0, flexShrink: 0, bgcolor: `${primary}40` }}>
                  {s.avatar
                    ? <Box component="img" src={s.avatar} alt={s.name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 700 }}>{s.name[0]}</Typography>
                      </Box>
                  }
                </Box>
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
              {speakers.map(s => `${s.name}${s.title ? `, ${s.title}` : ''}`).join(' · ')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Plenary Navbar ───────────────────────────────────────────────────────────
const PlenaryNavbar = ({ plenaryPage, event, primary, lobby }) => {
  const navigate = useNavigate();

  const backPath = event?.urlMode === 'flat'
    ? `/${lobby?.slug || 'lobby'}`
    : `/${event?.slug}/${lobby?.slug || 'lobby'}`;

  const pages = (lobby?.pages || []).filter(p => p.isActive);

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(0,0,0,0.06)', height: 56, px: { xs: 2, md: 4 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}
        sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 13, flexShrink: 0 }}>
        Back to Lobby
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {pages.map(p => {
          const isActive = p.id === 'plenary';
          const path = event?.urlMode === 'flat'
            ? `/${lobby?.slug || 'lobby'}/${p.slug}`
            : `/${event?.slug}/${lobby?.slug || 'lobby'}/${p.slug}`;
          return (
            <Button key={p.id} onClick={() => navigate(path)}
              sx={{
                fontSize: 14, px: 2, py: 0.75, borderRadius: 0,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? primary : 'text.secondary',
                borderBottom: isActive ? `2px solid ${primary}` : '2px solid transparent',
                '&:hover': { bgcolor: `${primary}08` },
              }}>
              {p.label}
            </Button>
          );
        })}
      </Box>

      <Button variant="contained"
        sx={{ bgcolor: primary, borderRadius: 100, px: 2.5, py: 0.75, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
        Need Help
      </Button>
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PLENARY PAGE
// ═════════════════════════════════════════════════════════════════════════════
const PlenaryPage = ({ preloadedPlenary, preloadedLobby }) => {
  const { event } = useSite();
  const plenaryPage = preloadedPlenary;
  const branding    = event?.branding || {};
  const primary     = branding.primaryColor || '#1565C0';

  const { authChecked } = useAuthGuard(event);
  usePageReady();

  const [lobby, setLobby] = useState(preloadedLobby || null);
  useEffect(() => {
    if (!lobby && plenaryPage?.event) {
      api.get(`/lobbies/public/${plenaryPage.event}`)
        .then(res => setLobby(res.data.data))
        .catch(() => {});
    }
  }, [plenaryPage]);

  const { session, status } = plenaryPage
    ? getClosestSession(event?.schedule || [], plenaryPage)
    : { session: null, status: 'offline' };

  if (!authChecked) return null;

  if (!plenaryPage) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0D1B2A' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Plenary not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', flexDirection: 'column', position: 'relative', overflow: 'hidden',
        ...(plenaryPage.bgType === 'image' && plenaryPage.bgUrl
          ? { backgroundImage: `url(${plenaryPage.bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { bgcolor: plenaryPage.bgColor || '#0D1B2A' }),
      }}>
      {/* Background video */}
      {plenaryPage.bgType === 'video' && plenaryPage.bgUrl && (
        <Box component="video" src={plenaryPage.bgUrl} autoPlay muted loop playsInline
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }} />
      )}

      {/* Dark overlay */}
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.6)', zIndex: 1 }} />

      {/* Content */}
      <Box sx={{ position: 'relative', width: '100%', maxWidth: 1300, zIndex: 2, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* pt: '56px' accounts for the fixed EventNavbar */}
        <Box sx={{ pt: '56px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, maxHeight: { lg: 'calc(100vh - 56px - 48px)' } }}>

            {/* Left: video + session info */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, md: 1 }, pt: { xs: 0, lg: 3 }, minWidth: 0, overflowY: 'auto' }}>
              <VideoPlayer
                streamUrl={plenaryPage.streamUrl}
                streamType={plenaryPage.streamType}
                viewerCount={plenaryPage.viewerCount}
                primary={primary}
              />
              <SessionInfo
                session={session}
                status={status}
                plenaryPage={plenaryPage}
                primary={primary}
                eventSpeakers={event?.speakers || []}
              />
            </Box>

            {/* Right: chat */}
            {plenaryPage.chatEnabled && (
              <Box sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0, p: { xs: 2, md: 1 }, pt: { xs: 0, lg: 3 }, display: 'flex', flexDirection: 'column' }}>
                <ChatPanel
                  plenaryPage={plenaryPage}
                  primary={primary}
                  chatEmbedUrl={plenaryPage.chatEmbedUrl}
                  chatTitle={plenaryPage.chatTitle}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlenaryPage;
